import { onScopeDispose, ref, watch } from 'vue';
import type { Ref } from 'vue';
import { v4 as uuidv4 } from 'uuid';
import { StoreSetupService } from '@/api/services/store-setup.service';
import { getServerErrorStatus } from '@/domain/reservations/reservation-errors';
import type {
  IGeoResult,
  IPlaceDetails,
  IPlaceSuggestion,
} from '@/interfaces/IStoreSetup';
import { i18n } from '@/plugins/i18n';

export const PLACES_SEARCH_DEBOUNCE_MS = 300;
export const PLACES_SEARCH_MIN_CHARS = 3;

export interface IUsePlacesSearch {
  /** The text of the search box. */
  query: Ref<string>;
  results: Ref<IPlaceSuggestion[]>;
  loading: Ref<boolean>;
  /** Consecutive autocomplete/details calls that failed because search is unavailable (503 or network). */
  failures: Ref<number>;
  /** Searches `query` 300 ms after the last call (from 3 characters). */
  search(): void;
  /** The place details of a suggestion; ends the Google session (the next search starts a new token). */
  pick(suggestion: IPlaceSuggestion): Promise<IPlaceDetails>;
  /** The typed address resolved in the selected country; null when not found; throws when search is unavailable. */
  geocode(address: string): Promise<IGeoResult | null>;
  /** The address at a point; null when not found or unavailable (it only prefills a field). */
  reverse(lat: number, lng: number): Promise<IGeoResult | null>;
}

/** 503 `PLACES_UNAVAILABLE`, a 5xx or no answer at all: what counts towards the manual-mode fallback. */
function isUnavailable(error: unknown): boolean {
  const status = getServerErrorStatus(error);
  return status === null || status >= 500;
}

/** The root of the admin locale (`pt-BR` → `pt`), Google's `languageCode`. */
function searchLanguage(): string {
  return i18n.locale.split('-')[0] || 'es';
}

/**
 * Place search of «Ubicación» (plan §4.1, §10.4) through the API's Google
 * proxy: debounced autocomplete with stale responses dropped by a request
 * counter, one Places session token per search that the details call ends,
 * and a count of consecutive failures for the manual fallback.
 */
export function usePlacesSearch(country: Ref<string>): IUsePlacesSearch {
  const query = ref('');
  const results = ref<IPlaceSuggestion[]>([]);
  const loading = ref(false);
  const failures = ref(0);
  let sessionToken: string | null = null;
  let requestNo = 0;
  let timer: ReturnType<typeof setTimeout> | null = null;
  let disposed = false;

  function currentToken(): string {
    if (!sessionToken) sessionToken = uuidv4();
    return sessionToken;
  }

  function cancelPending(): void {
    if (timer !== null) clearTimeout(timer);
    timer = null;
    requestNo++;
    loading.value = false;
  }

  async function runSearch(): Promise<void> {
    timer = null;
    const input = query.value.trim();
    const current = ++requestNo;
    if (input.length < PLACES_SEARCH_MIN_CHARS) {
      results.value = [];
      return;
    }
    loading.value = true;
    try {
      const suggestions = await StoreSetupService.placesAutocomplete({
        input,
        country: country.value,
        sessionToken: currentToken(),
        language: searchLanguage(),
      });
      if (disposed || current !== requestNo) return;
      results.value = suggestions;
      failures.value = 0;
    } catch (error) {
      if (disposed || current !== requestNo) return;
      results.value = [];
      if (isUnavailable(error)) failures.value++;
    } finally {
      if (!disposed && current === requestNo) loading.value = false;
    }
  }

  function search(): void {
    if (query.value.trim().length < PLACES_SEARCH_MIN_CHARS) {
      // Too short to search: the old suggestions go at once.
      cancelPending();
      results.value = [];
      return;
    }
    if (timer !== null) clearTimeout(timer);
    timer = setTimeout(() => void runSearch(), PLACES_SEARCH_DEBOUNCE_MS);
  }

  async function pick(suggestion: IPlaceSuggestion): Promise<IPlaceDetails> {
    cancelPending();
    const token = currentToken();
    // Google bills the autocomplete requests and the details call as one session.
    sessionToken = null;
    try {
      const details = await StoreSetupService.placeDetails(suggestion.placeId, {
        sessionToken: token,
        language: searchLanguage(),
      });
      if (!disposed) {
        failures.value = 0;
        results.value = [];
      }
      return details;
    } catch (error) {
      if (!disposed && isUnavailable(error)) failures.value++;
      throw error;
    }
  }

  function geocode(address: string): Promise<IGeoResult | null> {
    return StoreSetupService.geocode({
      address: address.trim(),
      country: country.value,
      language: searchLanguage(),
    });
  }

  async function reverse(lat: number, lng: number): Promise<IGeoResult | null> {
    try {
      return await StoreSetupService.reverseGeocode({
        lat,
        lng,
        language: searchLanguage(),
      });
    } catch {
      return null;
    }
  }

  // Another country makes the current suggestions and any answer in flight meaningless.
  watch(country, () => {
    cancelPending();
    results.value = [];
    sessionToken = null;
  });

  onScopeDispose(() => {
    disposed = true;
    if (timer !== null) clearTimeout(timer);
    timer = null;
    requestNo++;
  });

  return { query, results, loading, failures, search, pick, geocode, reverse };
}
